import React, { useEffect, useState } from 'react';
import { getUserCredits, UserCredits } from '../../api/credits';
import { useLanguage } from '../../contexts/LanguageContext';
import { Box, Typography, Chip, CircularProgress } from '@mui/material';
import { CreditCardOutlined as CreditIcon } from '@mui/icons-material';

interface CreditsDisplayProps {
  refreshTrigger?: number; // Increment to force refresh
  variant?: 'compact' | 'full'; // compact for header, full for detailed view
}

const CreditsDisplay: React.FC<CreditsDisplayProps> = ({ refreshTrigger = 0, variant = 'compact' }) => {
  const { t } = useLanguage();
  const [credits, setCredits] = useState<UserCredits | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const LOW_CREDITS_THRESHOLD = 5;

  const fetchCredits = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getUserCredits();

      if (data === null) {
        // User not logged in - don't show error, just show dash
        setLoading(false);
        return;
      }

      setCredits(data);
    } catch (err) {
      console.error('Failed to fetch credits:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCredits();
  }, [refreshTrigger]);

  if (loading) {
    return variant === 'compact' ? (
      <Chip
        icon={<CircularProgress size={14} />}
        label="..."
        size="small"
        variant="outlined"
        sx={{ borderRadius: 2 }}
      />
    ) : (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error || !credits) {
    return variant === 'compact' ? (
      <Chip
        icon={<CreditIcon />}
        label="—"
        size="small"
        variant="outlined"
        color="error"
        sx={{ borderRadius: 2 }}
      />
    ) : (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="error">
          {error || 'Error loading credits'}
        </Typography>
      </Box>
    );
  }

  const creditsNum = typeof credits.credits === 'number' ? credits.credits : 0;
  const isLowCredits = !credits.is_unlimited && creditsNum < LOW_CREDITS_THRESHOLD;
  const displayValue = credits.is_unlimited ? '∞' : credits.credits.toString();

  if (variant === 'compact') {
    return (
      <Chip
        icon={<CreditIcon />}
        label={`${displayValue} ${t('credits.label')}`}
        size="small"
        variant="outlined"
        color={isLowCredits ? 'warning' : 'primary'}
        sx={{
          borderRadius: 2,
          fontWeight: 600,
          ...(isLowCredits && {
            animation: 'pulse 2s infinite',
            '@keyframes pulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.7 }
            }
          })
        }}
      />
    );
  }

  // Full variant - detailed view
  return (
    <Box sx={{ p: 2, textAlign: 'center' }}>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        {t('credits.remaining')}
      </Typography>
      <Typography variant="h4" color={isLowCredits ? 'warning.main' : 'primary'} fontWeight={600}>
        {displayValue}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {t('credits.label')}
      </Typography>
      {!credits.is_unlimited && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          {t('credits.used')}: {credits.credits_used}
        </Typography>
      )}
      {isLowCredits && (
        <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 1 }}>
          ⚠️ {t('credits.lowWarning')}
        </Typography>
      )}
    </Box>
  );
};

export default CreditsDisplay;
